import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { ExternalLink, KeyRound, Copy, Check, Loader2 } from "lucide-react";

async function copyText(t) {
  try { await navigator.clipboard.writeText(t); return true; } catch { return false; }
}

/**
 * Schnell-Login (manuell): öffnet die Portal-Seite in neuem Tab und kopiert E-Mail bzw. Passwort in die Zwischenablage.
 * Zugangsdaten werden erst auf Klick vom Server geholt (nur Firmen-Inhaber, Zugriff wird protokolliert).
 */
export function PortalQuickLogin({ companyId, connectionId, compact = false }) {
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    if (creds) return creds;
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/receipt-connections/${connectionId}/reveal`);
      setCreds(r.data);
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zugangsdaten konnten nicht geladen werden");
      return null;
    } finally { setBusy(false); }
  };

  const openAndCopy = async (which) => {
    const c = await load();
    if (!c) return;
    const url = which === "invoices" && c.invoices_url ? c.invoices_url : c.login_url;
    if (url) window.open(url, "_blank", "noopener");
    if (c.username && await copyText(c.username)) {
      setCopied("username");
      toast.success(`${c.name}: E-Mail kopiert – im Portal einfügen, dann hier «Passwort kopieren» klicken`);
    }
  };

  const copy = async (field) => {
    const c = await load();
    if (!c || !c[field]) return;
    if (await copyText(c[field])) {
      setCopied(field);
      toast.success(field === "password" ? "Passwort kopiert (Zwischenablage)" : "E-Mail kopiert");
      setTimeout(() => setCopied(null), 4000);
    } else toast.error("Zwischenablage nicht verfügbar");
  };

  const btn = "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-medium disabled:opacity-50";
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid={`portal-quick-login-${connectionId}`}>
      <button type="button" disabled={busy} onClick={() => openAndCopy("login")} className={`${btn} border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100`}
        title="Login-Seite öffnen und E-Mail in die Zwischenablage kopieren" data-testid={`portal-quick-login-open-${connectionId}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />} Schnell-Login
      </button>
      {!compact && (
        <button type="button" disabled={busy} onClick={() => openAndCopy("invoices")} className={`${btn} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`}
          title="Rechnungsseite öffnen und E-Mail kopieren" data-testid={`portal-quick-login-invoices-${connectionId}`}>
          <ExternalLink className="h-3 w-3" /> Rechnungsseite
        </button>
      )}
      <button type="button" disabled={busy} onClick={() => copy("username")} className={`${btn} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`} data-testid={`portal-quick-login-copy-user-${connectionId}`}>
        {copied === "username" ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />} E-Mail
      </button>
      <button type="button" disabled={busy} onClick={() => copy("password")} className={`${btn} border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100`} data-testid={`portal-quick-login-copy-pass-${connectionId}`}>
        {copied === "password" ? <Check className="h-3 w-3 text-emerald-600" /> : <KeyRound className="h-3 w-3" />} Passwort kopieren
      </button>
    </span>
  );
}
